import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KIND_VARS = {
  otp: ["otp"],
  invoice: ["customer_name", "invoice_no", "grand_total", "paid_total", "branch_name", "items"],
  booking: ["customer_name", "pet_name", "service_name", "slot_start", "branch_name"],
  reminder: ["customer_name", "pet_name", "vaccine_name", "due_date"],
  generic: [],
};

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState({ name: "", kind: "generic", body: "", description: "", is_default: false });

  const load = () => api.get("/templates").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    setF(edit ? {
      name: edit.name, kind: edit.kind, body: edit.body,
      description: edit.description || "", is_default: !!edit.is_default,
    } : { name: "", kind: "generic", body: "", description: "", is_default: false });
  }, [edit, open]);

  const save = async () => {
    if (!f.name || !f.body) { toast.error("Name and body required"); return; }
    try {
      if (edit) await api.patch(`/templates/${edit.id}`, f);
      else await api.post("/templates", f);
      toast.success("Saved");
      setOpen(false); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/templates/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleApprove = async (t) => {
    try { await api.patch(`/templates/${t.id}`, { is_approved: !t.is_approved }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">WhatsApp</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Message templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Use <code className="bg-muted px-1 rounded text-xs">{`{{variable}}`}</code> placeholders. Required by WhatsApp Cloud API for messages sent outside 24-hr window.</p>
        </div>
        <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-template-btn"><Plus className="w-4 h-4 mr-1" />New template</Button>
      </div>

      <Card className="p-4">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Kind</TableHead><TableHead>Default</TableHead><TableHead>Approved</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(t => (
              <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell><Badge variant="outline">{t.kind}</Badge></TableCell>
                <TableCell>{t.is_default ? <Badge variant="secondary">default</Badge> : "—"}</TableCell>
                <TableCell>
                  <Switch checked={!!t.is_approved} onCheckedChange={() => toggleApprove(t)} data-testid={`approve-${t.id}`} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEdit(t); setOpen(true); }}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(t.id)}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No templates.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdit(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{edit ? "Edit" : "New"} template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} data-testid="tpl-name" /></div>
            <div><Label>Kind</Label>
              <Select value={f.kind} onValueChange={v => setF(p => ({ ...p, kind: v }))}>
                <SelectTrigger data-testid="tpl-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(KIND_VARS).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Body</Label>
              <Textarea rows={6} value={f.body} onChange={e => setF(p => ({ ...p, body: e.target.value }))} placeholder="Hi {{customer_name}}!..." data-testid="tpl-body" />
              {KIND_VARS[f.kind]?.length > 0 && (
                <div className="text-xs text-muted-foreground mt-1">Available vars: {KIND_VARS[f.kind].map(v => <code key={v} className="bg-muted px-1 rounded mr-1">{`{{${v}}}`}</code>)}</div>
              )}
            </div>
            <div><Label>Description (optional)</Label><Input value={f.description} onChange={e => setF(p => ({ ...p, description: e.target.value }))} /></div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.is_default} onCheckedChange={(v) => setF(p => ({ ...p, is_default: v }))} data-testid="tpl-default" />
              Use as default for this kind (replaces existing default)
            </label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-template">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
